import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import {
	type AssetRevision,
	type UpdateAssetRequest,
	addAssetPhoto,
	deleteAssetPhoto,
	getAssetRevisionHistory,
	updateAsset,
} from "@/services/asset-service";
import { auth } from "@/services/auth-service";
import {
	selfOrganizationEquipmentAreas,
	getOrganizationFormUniqueFields,
} from "@/services/organization-service";
import { uploadAssetPhoto } from "@/services/storage-service";
import {
	getManufacturers,
	getOrganizationAssetTypes,
} from "@/services/utility-service";
import {
	ArrowLeftOutlined,
	CalendarOutlined,
	CameraOutlined,
	CloseOutlined,
	DeleteOutlined,
	EditOutlined,
	EnvironmentOutlined,
	HistoryOutlined,
	InfoCircleOutlined,
	SaveOutlined,
	SettingOutlined,
	TagOutlined,
	ToolOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Card,
	Col,
	DatePicker,
	Descriptions,
	Image,
	Input,
	InputNumber,
	Modal,
	Progress,
	Row,
	Select,
	Space,
	Spin,
	Statistic,
	Tag,
	Timeline,
	Typography,
	Upload,
	message,
} from "antd";
import type { RcFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import React from "react";
import { useEffect, useRef, useState } from "react";
import "../PredSpendTab.scss";

const { Text } = Typography;

// ===================================
// INTERFACES
// ===================================

interface AssetDetailProps {
	assetId: string;
	onBack: () => void;
	backButtonText?: string;
	onNavigateToAsset?: (assetId: string) => void;
}

interface AssetAggregate {
	id: number;
	organizationId: number;
	locationId: number;
	organizationAssetTypeId: number;
	submittingUserId: number;
	formId: number;
	submissionId: number;
	dateOfBirth?: string;
	isDobEstimate?: boolean;
	tempAlertTag?: string;
	isTempAlertNotFound?: boolean;
	generalNotes?: string;
	isManufacturerNotFound?: boolean;
	isManufacturerPlateNotFound?: boolean;
	manufacturerDate?: string;
	isManufacturerDateNotFound?: boolean;
	modelId?: number;
	manufacturerId?: number; // Direct reference to manufacturer (independent of model)
	modelNumber?: string;
	isModelNotFound?: boolean;
	serialNumber?: string;
	isSerialNumberNotFound?: boolean;
	condition?: string;
	assetTag?: string;
	isRefrigeration?: boolean;
	isElectrical?: boolean;
	isDeleted?: string;
	isDecommissioned?: string;
	decommissionedReason?: string;
	lastAudit?: string;
	deletedBy?: number;
	powerSource?: string;
	organizationEquipmentAreaId?: number;
	organizationEquipmentSubAreaId?: number;
	uniqueFields?: any;
	externalFirebaseId?: string;
	source?: "VISION_CAPTURE" | "FORM_SUBMISSION" | "IMPORT";
	createdTimestamp: string;
	qaFlag?: boolean;
	updatedTimestamp: string;
	electricalDetails?: {
		amps?: number;
		voltage?: number;
		wattage?: number;
		isAmpsNotFound?: boolean;
		isVoltageNotFound?: boolean;
		isWattageNotFound?: boolean;
	};
	healthScore?: {
		healthScore?: number;
		replacementYear?: number;
		lastUpdate: string;
		remainingUsefulLife?: number;
	};
	refrigerationDetails?: {
		refrigerantType?: string;
		isRefrigerantNotFound?: boolean;
		refrigerantCharge?: number;
		refrigerantChargeMeasurementUnit?: string;
		isChargeNotFound?: boolean;
		usage?: string;
	};
	warranty?: {
		hasWarranty?: boolean;
		isWarrantyExpired?: boolean;
		isNotFound?: boolean;
		startDate?: string;
		expirationDate?: string;
		type?: string;
		term?: number;
		provider?: string;
		scProviderId?: number;
	};
	assetType?: {
		id: number;
		name: string;
	};
	assetTypeCategory?: {
		id: number;
		name: string;
	};
	equipmentArea?: {
		id: number;
		name: string;
	};
	equipmentSubArea?: {
		id: number;
		name: string;
	};
	location?: {
		id: number;
		name: string;
		address1?: string;
		address2?: string;
		city?: string;
		state?: string;
		zip?: string;
		country?: string;
	};
	manufacturer?: {
		id: number;
		name: string;
		isVerified: boolean;
		timeAdded: string;
	};
	model?: {
		id: number;
		manufacturerId: number;
		modelNumber: string;
		extra?: any;
		timeAdded: string;
	};
	photos: Array<{
		id: number;
		assetId: number;
		type: string;
		url: string;
	}>;
	generativeAi: Array<any>;
	integrationSyncs?: Array<{
		orgIntegrationId: number;
		syncStatus: string;
		lastSyncedAt?: string;
		syncDetails?: any;
	}>;
	photoSync?: {
		id: number;
		photoSyncJobId: number;
		assetId: number;
		syncStatus: string;
		photosUploaded: number;
		photosSkipped: number;
		photosFailed: number;
		lastSyncedAt?: string;
		syncDetails?: any;
		createdAt: string;
		updatedAt: string;
	};
	nychaElevator?: {
		elevator_id: number;
		elevator_bank_name: string;
		elevator_number: string;
		elevator_type: string;
		elevator_sub_type?: string;
		building_name?: string;
		wing_section?: string;
		floor?: string;
		oem?: string;
		elevator_model?: string;
		capacity?: number;
		maximo_asset_id?: string;
		maximo_status?: string;
		rems_asset_id?: string;
		rems_location_id?: string;
		last_periodic_inspection?: string;
		last_cat1_test?: string;
		last_cat5_test?: string;
		cab_photo_url?: string;
		active_alerts_count: number;
		open_violations_count: number;
	};
}

// ===================================
// COMPONENT
// ===================================

const AssetDetail = ({
	assetId,
	onBack,
	backButtonText = "Back to Asset Breakdown",
	onNavigateToAsset,
}: AssetDetailProps) => {
	// ===================================
	// STATE
	// ===================================

	const [loading, setLoading] = useState(true);
	const [asset, setAsset] = useState<AssetAggregate | null>(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [formValues, setFormValues] = useState<UpdateAssetRequest>({});
	const [saving, setSaving] = useState(false);

	// Dropdown options state
	const [assetTypes, setAssetTypes] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [equipmentAreas, setEquipmentAreas] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [manufacturers, setManufacturers] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [loadingOptions, setLoadingOptions] = useState(false);

	// Revision history state
	const [revisions, setRevisions] = useState<AssetRevision[]>([]);
	const [loadingRevisions, setLoadingRevisions] = useState(false);
	const [showRevisionHistory, setShowRevisionHistory] = useState(false);

	// Photo management state
	const [uploadingPhoto, setUploadingPhoto] = useState(false);
	const [photoType, setPhotoType] = useState<string>("OVERVIEW");

	// Refs to maintain cursor position in textareas
	const inputRefs = useRef<Record<string, any>>({});
	const focusedInputRef = useRef<string | null>(null);
	const cursorPositionRef = useRef<number>(0);

	// Org-level unique field definitions (for proper field rendering in edit mode)
	const [orgUniqueFieldDefs, setOrgUniqueFieldDefs] = useState<
		Array<{
			id: string;
			name: string;
			label: string;
			type: string;
			required?: boolean;
			placeholder?: string;
			options?: string[];
		}>
	>([]);

	// Duplicate error state
	const [duplicateError, setDuplicateError] = useState<{
		assetId: number;
		assetTag: string;
		locationInfo: string;
	} | null>(null);

	// ===================================
	// EFFECTS
	// ===================================

	useEffect(() => {
		fetchAssetDetail();
		if (isEditMode) {
			fetchDropdownOptions();
		}
	}, [assetId, isEditMode]);

	useEffect(() => {
		getOrganizationFormUniqueFields(3)
			.then((res) => {
				if (Array.isArray(res)) {
					setOrgUniqueFieldDefs(res);
				}
			})
			.catch(() => {});
	}, []);

	// Clear error and exit edit mode when asset changes
	useEffect(() => {
		setDuplicateError(null);
		setIsEditMode(false);
		setFormValues({});
	}, [assetId]);

	// Restore cursor position after formValues changes
	useEffect(() => {
		if (focusedInputRef.current && inputRefs.current[focusedInputRef.current]) {
			const inputRef = inputRefs.current[focusedInputRef.current];
			inputRef.focus();

			// Handle both Input and TextArea components
			const textElement =
				inputRef.resizableTextArea?.textArea || inputRef.input;
			if (textElement) {
				const position = cursorPositionRef.current;
				textElement.setSelectionRange(position, position);
			}
		}
	}, [formValues]);

	// ===================================
	// API FUNCTIONS
	// ===================================

	const fetchDropdownOptions = async () => {
		try {
			setLoadingOptions(true);
			const [assetTypesData, equipmentAreasData, manufacturersData] =
				await Promise.all([
					getOrganizationAssetTypes(),
					selfOrganizationEquipmentAreas(),
					getManufacturers(),
				]);

			setAssetTypes(assetTypesData);
			setEquipmentAreas(equipmentAreasData?.data || equipmentAreasData || []);
			setManufacturers(manufacturersData);
		} catch (error) {
			console.error("Failed to fetch dropdown options:", error);
			message.error("Failed to load dropdown options");
		} finally {
			setLoadingOptions(false);
		}
	};

	const fetchRevisionHistory = async () => {
		try {
			setLoadingRevisions(true);
			const history = await getAssetRevisionHistory(assetId);
			setRevisions(history);
		} catch (error) {
			console.error("Failed to fetch revision history:", error);
			message.error("Failed to load revision history");
		} finally {
			setLoadingRevisions(false);
		}
	};

	const fetchAssetDetail = async () => {
		try {
			setLoading(true);

			// Use the new asset aggregate endpoint
			const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/${assetId}`;

			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to fetch asset details (${response.status})`);
			}

			const result = await response.json();

			// Handle the response - extract data from success wrapper
			const assetData = result.success ? result.data : result;

			// Ensure photos is always an array, never null
			if (assetData && (!assetData.photos || assetData.photos === null)) {
				assetData.photos = [];
			} else if (
				assetData &&
				Array.isArray(assetData.photos) &&
				assetData.photos.length > 0
			) {
				// Ensure photos array items have required fields
				assetData.photos = assetData.photos
					.map((photo: any) => ({
						id: photo.id,
						type: photo.type,
						url: photo.url,
					}))
					.filter((photo: any) => photo.url); // Filter out photos without URLs
			}

			setAsset(assetData);
		} catch (error) {
			console.error("Failed to fetch asset details:", error);
			message.error("Failed to load asset details. Please try again.");
			setAsset(null);
		} finally {
			setLoading(false);
		}
	};

	// ===================================
	// EDIT MODE FUNCTIONS
	// ===================================

	const handleEditClick = () => {
		// Initialize form values with current asset data
		if (asset) {
			const baseValues: any = {
				assetTag: asset.assetTag ?? "",
				organizationAssetTypeId: asset.organizationAssetTypeId,
				condition: asset.condition ?? "",
				dateOfBirth: asset.dateOfBirth,
				isDobEstimate: asset.isDobEstimate,
				generalNotes: asset.generalNotes ?? "",
				manufacturerId: asset.manufacturer?.id,
				modelNumber: asset.modelNumber ?? "",
				serialNumber: asset.serialNumber ?? "",
				manufacturerDate: asset.manufacturerDate,
				tempAlertTag: asset.tempAlertTag ?? "",
				powerSource: asset.powerSource ?? "",
				organizationEquipmentAreaId: asset.organizationEquipmentAreaId,
			};

			// Initialize unique fields from asset data + org field definitions
			const existingUniqueFields = parseUniqueFields(asset.uniqueFields) || {};
			const mergedUniqueFields: Record<string, any> = { ...existingUniqueFields };
			for (const fieldDef of orgUniqueFieldDefs) {
				if (!(fieldDef.name in mergedUniqueFields)) {
					mergedUniqueFields[fieldDef.name] = "";
				}
			}
			if (Object.keys(mergedUniqueFields).length > 0) {
				baseValues.uniqueFields = mergedUniqueFields;
			}

			// Add NYCHA elevator fields if present
			if (asset.nychaElevator) {
				baseValues.nychaElevatorBankName =
					asset.nychaElevator.elevator_bank_name ?? "";
				baseValues.nychaElevatorNumber =
					asset.nychaElevator.elevator_number ?? "";
				baseValues.nychaElevatorType = asset.nychaElevator.elevator_type;
				baseValues.nychaElevatorSubType = asset.nychaElevator.elevator_sub_type;
				baseValues.nychaElevatorBuilding =
					asset.nychaElevator.building_name ?? "";
				baseValues.nychaElevatorWingSection =
					asset.nychaElevator.wing_section ?? "";
				baseValues.nychaElevatorFloor = asset.nychaElevator.floor ?? "";
				baseValues.nychaElevatorCapacity = asset.nychaElevator.capacity;
				baseValues.nychaMaximoAssetId =
					asset.nychaElevator.maximo_asset_id ?? "";
				baseValues.nychaRemsAssetId = asset.nychaElevator.rems_asset_id ?? "";
				baseValues.nychaRemsLocationId =
					asset.nychaElevator.rems_location_id ?? "";
				baseValues.nychaLastPeriodicInspection =
					asset.nychaElevator.last_periodic_inspection;
				baseValues.nychaLastCat1Test = asset.nychaElevator.last_cat1_test;
				baseValues.nychaLastCat5Test = asset.nychaElevator.last_cat5_test;
			}

			setFormValues(baseValues);
		}
		setIsEditMode(true);
		fetchDropdownOptions();
	};

	const handleCancelEdit = () => {
		setIsEditMode(false);
		setFormValues({});
		setDuplicateError(null);
	};

	const handleSave = async () => {
		try {
			setSaving(true);
			setDuplicateError(null); // Clear any previous errors

			// Separate NYCHA fields from regular asset fields
			const nychaFields: any = {};
			const assetFields: any = {};

			for (const [key, value] of Object.entries(formValues)) {
				if (key.startsWith("nycha")) {
					nychaFields[key] = value;
				} else {
					assetFields[key] = value;
				}
			}

			// Merge uniqueFields if they were edited
			const updatePayload: UpdateAssetRequest = { ...assetFields };
			if (assetFields.uniqueFields !== undefined) {
				// Merge with existing uniqueFields
				const currentUniqueFields =
					parseUniqueFields(asset?.uniqueFields) || {};
				const mergedUniqueFields = {
					...currentUniqueFields,
					...assetFields.uniqueFields,
				};
				updatePayload.uniqueFields = mergedUniqueFields;
			}

			// Update regular asset fields
			const updatedAsset = await updateAsset(assetId, updatePayload);

			// Update NYCHA elevator fields if any were changed
			if (Object.keys(nychaFields).length > 0 && asset?.nychaElevator) {
				const nychaPayload: any = {};

				// Only include fields that have values
				if (nychaFields.nychaElevatorBankName !== undefined)
					nychaPayload.elevatorBankName = nychaFields.nychaElevatorBankName;
				if (nychaFields.nychaElevatorNumber !== undefined)
					nychaPayload.elevatorNumber = nychaFields.nychaElevatorNumber;
				if (nychaFields.nychaElevatorType !== undefined)
					nychaPayload.elevatorType = nychaFields.nychaElevatorType;
				if (nychaFields.nychaElevatorSubType !== undefined)
					nychaPayload.elevatorSubType = nychaFields.nychaElevatorSubType;
				if (nychaFields.nychaElevatorBuilding !== undefined)
					nychaPayload.buildingName = nychaFields.nychaElevatorBuilding;
				if (nychaFields.nychaElevatorWingSection !== undefined)
					nychaPayload.wingSection = nychaFields.nychaElevatorWingSection;
				if (nychaFields.nychaElevatorFloor !== undefined)
					nychaPayload.floor = nychaFields.nychaElevatorFloor;
				if (nychaFields.nychaElevatorCapacity !== undefined)
					nychaPayload.capacity = nychaFields.nychaElevatorCapacity;
				if (nychaFields.nychaMaximoAssetId !== undefined)
					nychaPayload.maximoAssetId = nychaFields.nychaMaximoAssetId;
				if (nychaFields.nychaRemsAssetId !== undefined)
					nychaPayload.remsAssetId = nychaFields.nychaRemsAssetId;
				if (nychaFields.nychaRemsLocationId !== undefined)
					nychaPayload.remsLocationId = nychaFields.nychaRemsLocationId;
				if (nychaFields.nychaLastPeriodicInspection !== undefined)
					nychaPayload.lastPeriodicInspection =
						nychaFields.nychaLastPeriodicInspection;
				if (nychaFields.nychaLastCat1Test !== undefined)
					nychaPayload.lastCat1Test = nychaFields.nychaLastCat1Test;
				if (nychaFields.nychaLastCat5Test !== undefined)
					nychaPayload.lastCat5Test = nychaFields.nychaLastCat5Test;

				// Call NYCHA update endpoint
				const response = await fetch(
					`${VITE_CORE_API_URL}${CORE_URL}/nycha/elevator/asset/${assetId}`,
					{
						method: "PUT",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
						},
						body: JSON.stringify(nychaPayload),
					},
				);

				if (!response.ok) {
					const errorData = await response.json().catch(() => ({}));
					throw new Error(
						errorData.message || "Failed to update NYCHA elevator data",
					);
				}
			}

			// Refresh asset data
			await fetchAssetDetail();

			setIsEditMode(false);
			setFormValues({});
			message.success("Asset updated successfully");

			// Refresh revision history if it's being shown
			if (showRevisionHistory) {
				await fetchRevisionHistory();
			}
		} catch (error) {
			console.error("Failed to update asset:", error);

			// Parse duplicate asset tag error
			const errorMessage =
				error instanceof Error ? error.message : "Failed to update asset";

			if (errorMessage.startsWith("DUPLICATE_ASSET_TAG||")) {
				// Parse structured error: DUPLICATE_ASSET_TAG||assetId||assetTag||locationInfo
				const parts = errorMessage.split("||");
				if (parts.length === 4) {
					setDuplicateError({
						assetId: parseInt(parts[1]),
						assetTag: parts[2],
						locationInfo: parts[3],
					});
					window.scrollTo({ top: 0, behavior: "smooth" });
				} else {
					message.error("Asset tag already exists");
				}
			} else {
				message.error(errorMessage);
			}
		} finally {
			setSaving(false);
		}
	};

	const handleToggleRevisionHistory = async () => {
		if (!showRevisionHistory) {
			await fetchRevisionHistory();
		}
		setShowRevisionHistory(!showRevisionHistory);
	};

	const handlePhotoUpload = async (file: RcFile): Promise<boolean> => {
		if (!asset) return false;

		try {
			setUploadingPhoto(true);

			// Upload file to Firebase Storage
			const photoUrl = await uploadAssetPhoto(file, assetId);

			// Add photo record to database
			const newPhoto = await addAssetPhoto(assetId, photoUrl, photoType);

			// Update local asset state with new photo
			setAsset((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					photos: [...prev.photos, newPhoto],
				};
			});

			message.success("Photo uploaded successfully");
			return true;
		} catch (error) {
			console.error("Failed to upload photo:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to upload photo",
			);
			return false;
		} finally {
			setUploadingPhoto(false);
		}
	};

	const handlePhotoDelete = async (photoId: number) => {
		if (!asset) return;

		Modal.confirm({
			title: "Delete Photo",
			content: "Are you sure you want to delete this photo?",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deleteAssetPhoto(assetId, photoId);

					// Update local asset state to remove photo
					setAsset((prev) => {
						if (!prev) return prev;
						return {
							...prev,
							photos: prev.photos.filter((p) => p.id !== photoId),
						};
					});

					message.success("Photo deleted successfully");
				} catch (error) {
					console.error("Failed to delete photo:", error);
					message.error(
						error instanceof Error ? error.message : "Failed to delete photo",
					);
				}
			},
		});
	};

	// ===================================
	// UTILITY FUNCTIONS
	// ===================================

	const formatCurrency = (value?: number) => {
		if (!value) return "Not available";
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return "Not available";
		return dayjs(dateString).format("MM/DD/YYYY");
	};

	const parseUniqueFields = (uniqueFields?: any) => {
		if (!uniqueFields) return null;

		try {
			// Handle if uniqueFields is already a parsed object
			let parsed = uniqueFields;
			if (typeof uniqueFields === "string") {
				parsed = JSON.parse(uniqueFields);
			}

			return parsed;
		} catch (error) {
			console.warn("Failed to parse unique fields:", error);
			return null;
		}
	};

	const getConditionColor = (condition?: string) => {
		if (!condition) return "default";
		switch (condition.toLowerCase()) {
			case "excellent":
				return "green";
			case "good":
				return "blue";
			case "fair":
				return "orange";
			case "poor":
				return "red";
			case "critical":
				return "volcano";
			default:
				return "default";
		}
	};

	const getHealthScoreColor = (score?: number) => {
		if (!score) return "#d9d9d9";
		if (score > 60) return "#52c41a";
		if (score > 40) return "#faad14";
		return "#ff4d4f";
	};

	const calculateRemainingUsefulLife = () => {
		if (asset?.healthScore?.replacementYear) {
			const currentYear = new Date().getFullYear();
			const replacementYear = asset.healthScore.replacementYear;
			const remaining = replacementYear - currentYear;
			return remaining > 0 ? remaining : 0;
		}
		return asset?.healthScore?.remainingUsefulLife || null;
	};

	// ===================================
	// RENDER COMPONENTS
	// ===================================

	const SummaryCard = ({
		title,
		icon,
		children,
	}: {
		title: string;
		icon: React.ReactNode;
		children: React.ReactNode;
	}) => (
		<Card
			title={
				<Space>
					{icon}
					<span>{title}</span>
				</Space>
			}
			style={{ marginBottom: "16px" }}
		>
			{children}
		</Card>
	);

	// Helper function to render editable field
	const renderEditableField = (
		label: string,
		fieldName: keyof UpdateAssetRequest,
		value: any,
		type: "text" | "select" | "date" | "textarea" = "text",
		options?: Array<{ id: number; name: string }>,
		displayValue?: string,
	) => {
		if (!isEditMode) {
			return displayValue ?? value ?? "Not available";
		}

		const currentValue = formValues[fieldName] ?? value;

		switch (type) {
			case "select":
				return (
					<Select
						key={fieldName}
						value={currentValue}
						onChange={(val) =>
							setFormValues((prev) => ({ ...prev, [fieldName]: val }))
						}
						style={{ width: "100%" }}
						loading={loadingOptions}
						showSearch
						optionFilterProp="children"
						filterOption={(input, option) =>
							(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
						}
						options={options?.map((opt) => ({
							value: opt.id,
							label: opt.name,
						}))}
						placeholder="Select..."
					/>
				);
			case "date":
				return (
					<DatePicker
						key={fieldName}
						value={currentValue ? dayjs(currentValue) : null}
						onChange={(date) =>
							setFormValues((prev) => ({
								...prev,
								[fieldName]: date?.format("YYYY-MM-DD") || undefined,
							}))
						}
						style={{ width: "100%" }}
						format="MM/DD/YYYY"
					/>
				);
			case "textarea":
				return (
					<Input.TextArea
						key={fieldName}
						ref={(ref) => {
							if (ref) inputRefs.current[fieldName] = ref;
						}}
						value={currentValue}
						onChange={(e) => {
							const target = e.target;
							const position = target.selectionStart ?? 0;
							cursorPositionRef.current = position;
							focusedInputRef.current = fieldName;
							setFormValues((prev) => ({
								...prev,
								[fieldName]: e.target.value,
							}));
						}}
						onFocus={() => {
							focusedInputRef.current = fieldName;
						}}
						onSelect={(e: any) => {
							cursorPositionRef.current = e.target.selectionStart ?? 0;
						}}
						rows={4}
						placeholder="Enter notes..."
						dir="ltr"
						style={{ direction: "ltr", textAlign: "left" }}
					/>
				);
			default:
				return (
					<Input
						key={fieldName}
						ref={(ref) => {
							if (ref) inputRefs.current[fieldName] = ref;
						}}
						value={currentValue}
						onChange={(e) => {
							const target = e.target;
							const position = target.selectionStart ?? 0;
							cursorPositionRef.current = position;
							focusedInputRef.current = fieldName;
							setFormValues((prev) => ({
								...prev,
								[fieldName]: e.target.value,
							}));
						}}
						onFocus={() => {
							focusedInputRef.current = fieldName;
						}}
						onSelect={(e: any) => {
							cursorPositionRef.current = e.target.selectionStart ?? 0;
						}}
						placeholder={`Enter ${label.toLowerCase()}...`}
					/>
				);
		}
	};

	const renderPhotos = () => {
		const photoTypeOptions = [
			{ label: "Overview", value: "OVERVIEW" },
			{ label: "Asset Tag", value: "ASSET_TAG" },
			{ label: "Manufacturer Plate", value: "MANUFACTURER_PLATE" },
			{ label: "Front", value: "FRONT" },
			{ label: "Back", value: "BACK" },
			{ label: "Left", value: "LEFT" },
			{ label: "Right", value: "RIGHT" },
			{ label: "Other", value: "OTHER" },
		];

		return (
			<div>
				{/* Upload section - only visible in edit mode */}
				{isEditMode && (
					<div
						style={{
							marginBottom: "24px",
							padding: "16px",
							backgroundColor: "#f5f5f5",
							borderRadius: "8px",
						}}
					>
						<Space direction="vertical" style={{ width: "100%" }}>
							<Space>
								<span>Photo Type:</span>
								<Select
									value={photoType}
									onChange={setPhotoType}
									style={{ width: 200 }}
									options={photoTypeOptions}
								/>
							</Space>
							<Upload
								accept="image/*"
								showUploadList={false}
								customRequest={({ file, onSuccess, onError }) => {
									handlePhotoUpload(file as RcFile)
										.then((success) => {
											if (success) onSuccess?.("ok");
											else onError?.(new Error("Upload failed"));
										})
										.catch((err) => onError?.(err));
								}}
								disabled={uploadingPhoto}
							>
								<Button
									icon={<UploadOutlined />}
									loading={uploadingPhoto}
									type="primary"
								>
									{uploadingPhoto ? "Uploading..." : "Upload Photo"}
								</Button>
							</Upload>
						</Space>
					</div>
				)}

				{/* Photos grid */}
				{!asset?.photos || asset.photos.length === 0 ? (
					<div
						style={{
							textAlign: "center",
							padding: "20px",
							color: "#999",
							border: "1px dashed #d9d9d9",
							borderRadius: "6px",
						}}
					>
						<CameraOutlined style={{ fontSize: "24px", marginBottom: "8px" }} />
						<div>No photos available</div>
					</div>
				) : (
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
							gap: "16px",
						}}
					>
						{asset.photos.map((photo, index) => (
							<div
								key={photo.id || index}
								style={{ position: "relative", textAlign: "center" }}
							>
								<Image
									src={photo.url}
									alt={`Asset photo - ${photo.type}`}
									style={{
										width: "100%",
										height: "150px",
										objectFit: "cover",
										borderRadius: "6px",
									}}
									preview={{
										mask: <div>Click to preview</div>,
									}}
								/>
								<div
									style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}
								>
									{photo.type
										.replace(/_/g, " ")
										.toLowerCase()
										.replace(/\b\w/g, (l) => l.toUpperCase())}
								</div>
								{/* Delete button - only visible in edit mode */}
								{isEditMode && (
									<Button
										danger
										size="small"
										icon={<DeleteOutlined />}
										onClick={() => handlePhotoDelete(photo.id)}
										style={{
											position: "absolute",
											top: "8px",
											right: "8px",
											zIndex: 1,
										}}
									/>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		);
	};

	// ===================================
	// RENDER
	// ===================================

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" tip="Loading asset details..." />
			</div>
		);
	}

	if (!asset) {
		return (
			<div className="asset-detail">
				{/* =============== HEADER SECTION =============== */}
				<div className="pred-spend-header">
					<div className="header-content">
						<div className="header-title">
							<h2>Asset Not Found</h2>
							<p>The requested asset could not be found</p>
						</div>
						<div className="header-actions">
							<Space>
								<Button icon={<ArrowLeftOutlined />} onClick={onBack}>
									{backButtonText}
								</Button>
							</Space>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="asset-detail">
			{/* =============== DUPLICATE ERROR BANNER =============== */}
			{duplicateError && (
				<Alert
					message="Duplicate Asset Tag Error"
					description={
						<div>
							Asset tag <strong>{duplicateError.assetTag}</strong> already
							exists on{" "}
							<a
								href={`#`}
								onClick={(e) => {
									e.preventDefault();
									if (onNavigateToAsset) {
										onNavigateToAsset(duplicateError.assetId.toString());
									}
								}}
								style={{
									fontWeight: "bold",
									textDecoration: "underline",
									cursor: "pointer",
								}}
							>
								Asset #{duplicateError.assetId}
							</a>{" "}
							at location: <strong>{duplicateError.locationInfo}</strong>
						</div>
					}
					type="error"
					closable
					onClose={() => setDuplicateError(null)}
					showIcon
					style={{ marginBottom: "24px" }}
				/>
			)}

			{/* =============== HEADER SECTION =============== */}
			<div className="pred-spend-header">
				<div className="header-content">
					<div className="header-title">
						<h2>Asset Details</h2>
						<p>
							{asset.assetTag
								? `Asset Tag: ${asset.assetTag}`
								: `Asset ID: ${asset.id}`}
						</p>
					</div>
					<div className="header-actions">
						<Space>
							{!isEditMode ? (
								<>
									<Button
										icon={<HistoryOutlined />}
										onClick={handleToggleRevisionHistory}
										type={showRevisionHistory ? "primary" : "default"}
									>
										{showRevisionHistory ? "Hide" : "Show"} Revision History
									</Button>
									<Button
										icon={<EditOutlined />}
										onClick={handleEditClick}
										type="primary"
									>
										Edit
									</Button>
								</>
							) : (
								<>
									<Button
										icon={<CloseOutlined />}
										onClick={handleCancelEdit}
										disabled={saving}
									>
										Cancel
									</Button>
									<Button
										icon={<SaveOutlined />}
										onClick={handleSave}
										type="primary"
										loading={saving}
									>
										Save
									</Button>
								</>
							)}
							<Button icon={<ArrowLeftOutlined />} onClick={onBack}>
								{backButtonText}
							</Button>
						</Space>
					</div>
				</div>
			</div>

			{/* =============== SUMMARY STATISTICS =============== */}
			<Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
				<Col xs={24} sm={12} md={6}>
					<Card>
						<Statistic
							title="Health Score"
							value={asset.healthScore?.healthScore || 0}
							suffix="%"
							valueStyle={{
								color: getHealthScoreColor(asset.healthScore?.healthScore),
							}}
							prefix={
								<Progress
									type="circle"
									percent={asset.healthScore?.healthScore || 0}
									size={20}
									strokeColor={getHealthScoreColor(
										asset.healthScore?.healthScore,
									)}
									showInfo={false}
								/>
							}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<Card>
						<Statistic
							title="Condition"
							value={asset.condition || "Unknown"}
							prefix={<ToolOutlined />}
							valueStyle={{
								color:
									getConditionColor(asset.condition) === "green"
										? "#52c41a"
										: getConditionColor(asset.condition) === "blue"
											? "#1890ff"
											: getConditionColor(asset.condition) === "orange"
												? "#faad14"
												: getConditionColor(asset.condition) === "red"
													? "#ff4d4f"
													: "#999",
							}}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<Card>
						<Statistic
							title="Replacement Year"
							value={asset.healthScore?.replacementYear || "TBD"}
							prefix={<CalendarOutlined />}
							formatter={(value) => value?.toString()}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<Card>
						<Statistic
							title="Remaining Life"
							value={calculateRemainingUsefulLife() ?? "Unknown"}
							suffix="years"
							prefix={<InfoCircleOutlined />}
						/>
					</Card>
				</Col>
			</Row>

			{/* =============== ASSET INFORMATION =============== */}
			<Row gutter={[16, 16]}>
				<Col xs={24} lg={12}>
					<SummaryCard title="Basic Information" icon={<InfoCircleOutlined />}>
						<Descriptions column={1} bordered size="small">
							<Descriptions.Item label="Asset Tag">
								{renderEditableField("Asset Tag", "assetTag", asset.assetTag)}
							</Descriptions.Item>
							<Descriptions.Item label="Asset Type">
								{renderEditableField(
									"Asset Type",
									"organizationAssetTypeId",
									asset.organizationAssetTypeId,
									"select",
									assetTypes,
									asset.assetType?.name,
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Asset Category">
								{asset.assetTypeCategory?.name || "Unknown"}
							</Descriptions.Item>
							<Descriptions.Item label="Condition">
								{isEditMode ? (
									renderEditableField("Condition", "condition", asset.condition)
								) : (
									<Tag color={getConditionColor(asset.condition)}>
										{asset.condition || "Unknown"}
									</Tag>
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Date of Birth">
								{renderEditableField(
									"Date of Birth",
									"dateOfBirth",
									asset.dateOfBirth,
									"date",
									undefined,
									formatDate(asset.dateOfBirth),
								)}
								{!isEditMode && asset.isDobEstimate && (
									<Tag color="orange" style={{ marginLeft: "8px" }}>
										Estimated
									</Tag>
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Created">
								{formatDate(asset.createdTimestamp)}
							</Descriptions.Item>
							<Descriptions.Item label="Last Updated">
								{formatDate(asset.updatedTimestamp)}
							</Descriptions.Item>
							<Descriptions.Item label="Source">
								<Tag
									color={
										asset.source === "VISION_CAPTURE"
											? "blue"
											: asset.source === "IMPORT"
												? "green"
												: "default"
									}
								>
									{asset.source === "VISION_CAPTURE"
										? "Vision Capture"
										: asset.source === "IMPORT"
											? "Import"
											: "Form Submission"}
								</Tag>
							</Descriptions.Item>
						</Descriptions>
					</SummaryCard>
				</Col>

				<Col xs={24} lg={12}>
					<SummaryCard
						title="Location & Equipment Area"
						icon={<EnvironmentOutlined />}
					>
						<Descriptions column={1} bordered size="small">
							<Descriptions.Item label="Location">
								{asset.location?.name || "Unknown"}
							</Descriptions.Item>
							{asset.location?.address1 && (
								<Descriptions.Item label="Address">
									{`${asset.location.address1}${asset.location.address2 ? `, ${asset.location.address2}` : ""}`}
								</Descriptions.Item>
							)}
							{(asset.location?.city ||
								asset.location?.state ||
								asset.location?.zip) && (
								<Descriptions.Item label="City, State ZIP">
									{`${asset.location?.city || ""} ${asset.location?.state || ""} ${asset.location?.zip || ""}`.trim()}
								</Descriptions.Item>
							)}
							<Descriptions.Item label="Equipment Area">
								{renderEditableField(
									"Equipment Area",
									"organizationEquipmentAreaId",
									asset.organizationEquipmentAreaId,
									"select",
									equipmentAreas,
									asset.equipmentArea?.name,
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Equipment Sub Area">
								{asset.equipmentSubArea?.name || "Not specified"}
							</Descriptions.Item>
							<Descriptions.Item label="Power Source">
								{renderEditableField(
									"Power Source",
									"powerSource",
									asset.powerSource,
								)}
							</Descriptions.Item>
						</Descriptions>
					</SummaryCard>
				</Col>
			</Row>

			{/* =============== MANUFACTURER & MODEL INFORMATION =============== */}
			<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
				<Col xs={24} lg={12}>
					<SummaryCard title="Manufacturer & Model" icon={<TagOutlined />}>
						<Descriptions column={1} bordered size="small">
							<Descriptions.Item label="Manufacturer">
								{renderEditableField(
									"Manufacturer",
									"manufacturerId",
									asset.manufacturer?.id,
									"select",
									manufacturers,
									asset.manufacturer?.name,
								)}
								{!isEditMode && asset.isManufacturerNotFound && (
									<Tag color="red" style={{ marginLeft: "8px" }}>
										Not Found
									</Tag>
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Model Number">
								{renderEditableField(
									"Model Number",
									"modelNumber",
									asset.modelNumber,
								)}
								{!isEditMode && asset.isModelNotFound && (
									<Tag color="red" style={{ marginLeft: "8px" }}>
										Not Found
									</Tag>
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Serial Number">
								{renderEditableField(
									"Serial Number",
									"serialNumber",
									asset.serialNumber,
								)}
								{!isEditMode && asset.isSerialNumberNotFound && (
									<Tag color="red" style={{ marginLeft: "8px" }}>
										Not Found
									</Tag>
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Manufacturer Date">
								{renderEditableField(
									"Manufacturer Date",
									"manufacturerDate",
									asset.manufacturerDate,
									"date",
									undefined,
									formatDate(asset.manufacturerDate),
								)}
								{!isEditMode && asset.isManufacturerDateNotFound && (
									<Tag color="red" style={{ marginLeft: "8px" }}>
										Not Found
									</Tag>
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Temp Alert Tag">
								{renderEditableField(
									"Temp Alert Tag",
									"tempAlertTag",
									asset.tempAlertTag,
								)}
								{!isEditMode && asset.isTempAlertNotFound && (
									<Tag color="red" style={{ marginLeft: "8px" }}>
										Not Found
									</Tag>
								)}
							</Descriptions.Item>
						</Descriptions>
					</SummaryCard>
				</Col>

				<Col xs={24} lg={12}>
					<SummaryCard title="Health & Maintenance" icon={<ToolOutlined />}>
						<Descriptions column={1} bordered size="small">
							<Descriptions.Item label="Health Score">
								<div
									style={{ display: "flex", alignItems: "center", gap: "8px" }}
								>
									<Progress
										percent={asset.healthScore?.healthScore || 0}
										size="small"
										strokeColor={getHealthScoreColor(
											asset.healthScore?.healthScore,
										)}
										style={{ width: "100px" }}
										showInfo={false}
									/>
									<span>{asset.healthScore?.healthScore || 0}%</span>
								</div>
							</Descriptions.Item>
							<Descriptions.Item label="Replacement Year">
								{asset.healthScore?.replacementYear
									? `${asset.healthScore.replacementYear}`
									: "Not calculated"}
							</Descriptions.Item>
							<Descriptions.Item label="Remaining Useful Life">
								{calculateRemainingUsefulLife() !== null
									? `${calculateRemainingUsefulLife()} years`
									: "Not calculated"}
							</Descriptions.Item>
							<Descriptions.Item label="Last Health Update">
								{formatDate(asset.healthScore?.lastUpdate)}
							</Descriptions.Item>
							<Descriptions.Item label="Last Audit">
								{formatDate(asset.lastAudit)}
							</Descriptions.Item>
						</Descriptions>
					</SummaryCard>
				</Col>
			</Row>

			{/* =============== NYCHA ELEVATOR INFORMATION =============== */}
			{asset.nychaElevator && (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					<Col xs={24}>
						<SummaryCard
							title="NYCHA Elevator Information"
							icon={<SettingOutlined />}
						>
							<Row gutter={[16, 16]}>
								<Col xs={24} lg={12}>
									<Descriptions
										column={1}
										bordered
										size="small"
										title="Elevator Details"
									>
										<Descriptions.Item label="Elevator Bank">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaElevatorBankName"] = ref;
													}}
													value={
														(formValues as any).nychaElevatorBankName ??
														asset.nychaElevator.elevator_bank_name ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaElevatorBankName";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorBankName: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaElevatorBankName";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter elevator bank name..."
												/>
											) : (
												asset.nychaElevator.elevator_bank_name ||
												"Not available"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Elevator Number">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaElevatorNumber"] = ref;
													}}
													value={
														(formValues as any).nychaElevatorNumber ??
														asset.nychaElevator.elevator_number ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaElevatorNumber";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorNumber: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaElevatorNumber";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter elevator number..."
												/>
											) : (
												asset.nychaElevator.elevator_number || "Not available"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Type">
											{isEditMode ? (
												<Select
													value={
														(formValues as any).nychaElevatorType ??
														asset.nychaElevator.elevator_type
													}
													onChange={(val) =>
														setFormValues(
															(prev) =>
																({ ...prev, nychaElevatorType: val }) as any,
														)
													}
													style={{ width: "100%" }}
													options={[
														{ label: "Passenger", value: "PASSENGER" },
														{ label: "Freight", value: "FREIGHT" },
														{ label: "Service", value: "SERVICE" },
													]}
												/>
											) : (
												<Tag color="blue">
													{asset.nychaElevator.elevator_type}
												</Tag>
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Sub Type">
											{isEditMode ? (
												<Select
													value={
														(formValues as any).nychaElevatorSubType ??
														asset.nychaElevator.elevator_sub_type
													}
													onChange={(val) =>
														setFormValues(
															(prev) =>
																({ ...prev, nychaElevatorSubType: val }) as any,
														)
													}
													style={{ width: "100%" }}
													allowClear
													placeholder="Select sub type..."
													options={[
														{ label: "Traction", value: "TRACTION" },
														{ label: "Hydraulic", value: "HYDRAULIC" },
													]}
												/>
											) : asset.nychaElevator.elevator_sub_type ? (
												<Tag>{asset.nychaElevator.elevator_sub_type}</Tag>
											) : (
												"Not specified"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Building">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaElevatorBuilding"] = ref;
													}}
													value={
														(formValues as any).nychaElevatorBuilding ??
														asset.nychaElevator.building_name ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaElevatorBuilding";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorBuilding: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaElevatorBuilding";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter building name..."
												/>
											) : (
												asset.nychaElevator.building_name || "Not specified"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Wing/Section">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaElevatorWingSection"] =
																ref;
													}}
													value={
														(formValues as any).nychaElevatorWingSection ??
														asset.nychaElevator.wing_section ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current =
															"nychaElevatorWingSection";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorWingSection: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current =
															"nychaElevatorWingSection";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter wing/section..."
												/>
											) : (
												asset.nychaElevator.wing_section || "Not specified"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Floor">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaElevatorFloor"] = ref;
													}}
													value={
														(formValues as any).nychaElevatorFloor ??
														asset.nychaElevator.floor ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaElevatorFloor";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorFloor: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaElevatorFloor";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter floor..."
												/>
											) : (
												asset.nychaElevator.floor || "Not specified"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Capacity (lbs)">
											{isEditMode ? (
												<InputNumber
													value={
														(formValues as any).nychaElevatorCapacity ??
														asset.nychaElevator.capacity
													}
													onChange={(val) =>
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaElevatorCapacity: val,
																}) as any,
														)
													}
													style={{ width: "100%" }}
													placeholder="Enter capacity..."
												/>
											) : asset.nychaElevator.capacity ? (
												`${asset.nychaElevator.capacity} lbs`
											) : (
												"Not specified"
											)}
										</Descriptions.Item>
									</Descriptions>
								</Col>
								<Col xs={24} lg={12}>
									<Descriptions
										column={1}
										bordered
										size="small"
										title="Maximo Status"
									>
										<Descriptions.Item label="Status">
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<Tag
													color={
														asset.nychaElevator.maximo_status === "ACTIVE"
															? "green"
															: asset.nychaElevator.maximo_status === "DOWN"
																? "red"
																: asset.nychaElevator.maximo_status ===
																		"MAINTENANCE"
																	? "orange"
																	: "default"
													}
												>
													{asset.nychaElevator.maximo_status
														? asset.nychaElevator.maximo_status === "ACTIVE"
															? "● IN SERVICE"
															: asset.nychaElevator.maximo_status === "DOWN"
																? "● OUT OF SERVICE"
																: asset.nychaElevator.maximo_status
														: "Not Connected"}
												</Tag>
												<Button
													size="small"
													icon={<ToolOutlined />}
													onClick={() =>
														message.info("Refreshing Maximo status...")
													}
												>
													Refresh
												</Button>
											</div>
										</Descriptions.Item>
										<Descriptions.Item label="Maximo Asset ID">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaMaximoAssetId"] = ref;
													}}
													value={
														(formValues as any).nychaMaximoAssetId ??
														asset.nychaElevator.maximo_asset_id ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaMaximoAssetId";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaMaximoAssetId: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaMaximoAssetId";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter Maximo Asset ID..."
												/>
											) : (
												asset.nychaElevator.maximo_asset_id || "Not assigned"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Active Alerts">
											<Tag
												color={
													asset.nychaElevator.active_alerts_count > 0
														? "red"
														: "green"
												}
											>
												{asset.nychaElevator.active_alerts_count} alert
												{asset.nychaElevator.active_alerts_count !== 1
													? "s"
													: ""}
											</Tag>
										</Descriptions.Item>
										<Descriptions.Item label="Open Violations">
											<Tag
												color={
													asset.nychaElevator.open_violations_count > 0
														? "orange"
														: "green"
												}
											>
												{asset.nychaElevator.open_violations_count} violation
												{asset.nychaElevator.open_violations_count !== 1
													? "s"
													: ""}
											</Tag>
										</Descriptions.Item>
									</Descriptions>
								</Col>
								<Col xs={24} lg={12}>
									<Descriptions
										column={1}
										bordered
										size="small"
										title="REMS Information"
									>
										<Descriptions.Item label="REMS Status">
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<Tag
													color={
														asset.nychaElevator.rems_asset_id
															? "blue"
															: "default"
													}
												>
													{asset.nychaElevator.rems_asset_id
														? "Connected"
														: "Not Connected"}
												</Tag>
												<Button
													size="small"
													icon={<ToolOutlined />}
													onClick={() =>
														message.info("Refreshing REMS data...")
													}
												>
													Refresh
												</Button>
											</div>
										</Descriptions.Item>
										<Descriptions.Item label="REMS Asset ID">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaRemsAssetId"] = ref;
													}}
													value={
														(formValues as any).nychaRemsAssetId ??
														asset.nychaElevator.rems_asset_id ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaRemsAssetId";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaRemsAssetId: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaRemsAssetId";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter REMS Asset ID..."
												/>
											) : (
												asset.nychaElevator.rems_asset_id || "Not assigned"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="REMS Location ID">
											{isEditMode ? (
												<Input
													ref={(ref) => {
														if (ref)
															inputRefs.current["nychaRemsLocationId"] = ref;
													}}
													value={
														(formValues as any).nychaRemsLocationId ??
														asset.nychaElevator.rems_location_id ??
														""
													}
													onChange={(e) => {
														const target = e.target;
														const position = target.selectionStart ?? 0;
														cursorPositionRef.current = position;
														focusedInputRef.current = "nychaRemsLocationId";
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaRemsLocationId: e.target.value,
																}) as any,
														);
													}}
													onFocus={() => {
														focusedInputRef.current = "nychaRemsLocationId";
													}}
													onSelect={(e: any) => {
														cursorPositionRef.current =
															e.target.selectionStart ?? 0;
													}}
													placeholder="Enter REMS Location ID..."
												/>
											) : (
												asset.nychaElevator.rems_location_id || "Not assigned"
											)}
										</Descriptions.Item>
									</Descriptions>
								</Col>
								<Col xs={24} lg={12}>
									<Descriptions
										column={1}
										bordered
										size="small"
										title="Compliance & Testing"
									>
										<Descriptions.Item label="Last Periodic Inspection">
											{isEditMode ? (
												<DatePicker
													value={
														(formValues as any).nychaLastPeriodicInspection
															? dayjs(
																	(formValues as any)
																		.nychaLastPeriodicInspection,
																)
															: asset.nychaElevator.last_periodic_inspection
																? dayjs(
																		asset.nychaElevator
																			.last_periodic_inspection,
																	)
																: null
													}
													onChange={(date) =>
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaLastPeriodicInspection:
																		date?.format("YYYY-MM-DD") || undefined,
																}) as any,
														)
													}
													style={{ width: "100%" }}
													format="MM/DD/YYYY"
												/>
											) : asset.nychaElevator.last_periodic_inspection ? (
												formatDate(asset.nychaElevator.last_periodic_inspection)
											) : (
												"No data"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Last Cat 1 Test">
											{isEditMode ? (
												<DatePicker
													value={
														(formValues as any).nychaLastCat1Test
															? dayjs((formValues as any).nychaLastCat1Test)
															: asset.nychaElevator.last_cat1_test
																? dayjs(asset.nychaElevator.last_cat1_test)
																: null
													}
													onChange={(date) =>
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaLastCat1Test:
																		date?.format("YYYY-MM-DD") || undefined,
																}) as any,
														)
													}
													style={{ width: "100%" }}
													format="MM/DD/YYYY"
												/>
											) : asset.nychaElevator.last_cat1_test ? (
												formatDate(asset.nychaElevator.last_cat1_test)
											) : (
												"No data"
											)}
										</Descriptions.Item>
										<Descriptions.Item label="Last Cat 5 Test">
											{isEditMode ? (
												<DatePicker
													value={
														(formValues as any).nychaLastCat5Test
															? dayjs((formValues as any).nychaLastCat5Test)
															: asset.nychaElevator.last_cat5_test
																? dayjs(asset.nychaElevator.last_cat5_test)
																: null
													}
													onChange={(date) =>
														setFormValues(
															(prev) =>
																({
																	...prev,
																	nychaLastCat5Test:
																		date?.format("YYYY-MM-DD") || undefined,
																}) as any,
														)
													}
													style={{ width: "100%" }}
													format="MM/DD/YYYY"
												/>
											) : asset.nychaElevator.last_cat5_test ? (
												formatDate(asset.nychaElevator.last_cat5_test)
											) : (
												"No data"
											)}
										</Descriptions.Item>
									</Descriptions>
								</Col>
							</Row>
						</SummaryCard>
					</Col>
				</Row>
			)}

			{/* =============== TECHNICAL SPECIFICATIONS =============== */}
			{(asset.isElectrical || asset.isRefrigeration) && (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					{asset.isElectrical && asset.electricalDetails && (
						<Col xs={24} lg={12}>
							<SummaryCard title="Electrical Details" icon={<ToolOutlined />}>
								<Descriptions column={1} bordered size="small">
									<Descriptions.Item label="Amps">
										{asset.electricalDetails.amps || "Not specified"}
										{asset.electricalDetails.isAmpsNotFound && (
											<Tag color="red" style={{ marginLeft: "8px" }}>
												Not Found
											</Tag>
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Voltage">
										{asset.electricalDetails.voltage || "Not specified"}
										{asset.electricalDetails.isVoltageNotFound && (
											<Tag color="red" style={{ marginLeft: "8px" }}>
												Not Found
											</Tag>
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Wattage">
										{asset.electricalDetails.wattage || "Not specified"}
										{asset.electricalDetails.isWattageNotFound && (
											<Tag color="red" style={{ marginLeft: "8px" }}>
												Not Found
											</Tag>
										)}
									</Descriptions.Item>
								</Descriptions>
							</SummaryCard>
						</Col>
					)}

					{asset.isRefrigeration && asset.refrigerationDetails && (
						<Col xs={24} lg={12}>
							<SummaryCard
								title="Refrigeration Details"
								icon={<ToolOutlined />}
							>
								<Descriptions column={1} bordered size="small">
									<Descriptions.Item label="Refrigerant Type">
										{asset.refrigerationDetails.refrigerantType ||
											"Not specified"}
										{asset.refrigerationDetails.isRefrigerantNotFound && (
											<Tag color="red" style={{ marginLeft: "8px" }}>
												Not Found
											</Tag>
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Refrigerant Charge">
										{asset.refrigerationDetails.refrigerantCharge &&
										asset.refrigerationDetails.refrigerantChargeMeasurementUnit
											? `${asset.refrigerationDetails.refrigerantCharge} ${asset.refrigerationDetails.refrigerantChargeMeasurementUnit}`
											: "Not specified"}
										{asset.refrigerationDetails.isChargeNotFound && (
											<Tag color="red" style={{ marginLeft: "8px" }}>
												Not Found
											</Tag>
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Usage">
										{asset.refrigerationDetails.usage || "Not specified"}
									</Descriptions.Item>
								</Descriptions>
							</SummaryCard>
						</Col>
					)}
				</Row>
			)}

			{/* =============== WARRANTY INFORMATION =============== */}
			{(asset.warranty || isEditMode) && (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					<Col xs={24}>
						<SummaryCard
							title="Warranty Information"
							icon={<InfoCircleOutlined />}
						>
							{isEditMode ? (
								<Row gutter={16}>
									<Col span={8}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Has Warranty
											</label>
											<Select
												value={
													formValues.warranty?.hasWarranty ??
													asset.warranty?.hasWarranty ??
													undefined
												}
												onChange={(value) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															hasWarranty: value,
														},
													}))
												}
												style={{ width: "100%" }}
												placeholder="Select"
											>
												<Select.Option value={true}>Yes</Select.Option>
												<Select.Option value={false}>No</Select.Option>
											</Select>
										</div>
									</Col>
									<Col span={8}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Warranty Status
											</label>
											<Select
												value={
													formValues.warranty?.isWarrantyExpired ??
													asset.warranty?.isWarrantyExpired ??
													undefined
												}
												onChange={(value) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															isWarrantyExpired: value,
														},
													}))
												}
												style={{ width: "100%" }}
												placeholder="Select status"
											>
												<Select.Option value={false}>Active</Select.Option>
												<Select.Option value={true}>Expired</Select.Option>
											</Select>
										</div>
									</Col>
									<Col span={8}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Not Found
											</label>
											<Select
												value={
													formValues.warranty?.isNotFound ??
													asset.warranty?.isNotFound ??
													undefined
												}
												onChange={(value) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															isNotFound: value,
														},
													}))
												}
												style={{ width: "100%" }}
												placeholder="Select"
											>
												<Select.Option value={false}>Found</Select.Option>
												<Select.Option value={true}>Not Found</Select.Option>
											</Select>
										</div>
									</Col>
									<Col span={12}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Start Date
											</label>
											<DatePicker
												value={
													formValues.warranty?.startDate
														? dayjs(formValues.warranty.startDate)
														: asset.warranty?.startDate
															? dayjs(asset.warranty.startDate)
															: undefined
												}
												onChange={(date) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															startDate: date?.format("YYYY-MM-DD"),
														},
													}))
												}
												style={{ width: "100%" }}
												format="YYYY-MM-DD"
											/>
										</div>
									</Col>
									<Col span={12}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Expiration Date
											</label>
											<DatePicker
												value={
													formValues.warranty?.expirationDate
														? dayjs(formValues.warranty.expirationDate)
														: asset.warranty?.expirationDate
															? dayjs(asset.warranty.expirationDate)
															: undefined
												}
												onChange={(date) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															expirationDate: date?.format("YYYY-MM-DD"),
														},
													}))
												}
												style={{ width: "100%" }}
												format="YYYY-MM-DD"
											/>
										</div>
									</Col>
									<Col span={12}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Warranty Type
											</label>
											<Input
												ref={(ref) => {
													if (ref) inputRefs.current["warranty.type"] = ref;
												}}
												value={
													formValues.warranty?.type ??
													asset.warranty?.type ??
													""
												}
												onChange={(e) => {
													const target = e.target;
													const position = target.selectionStart ?? 0;
													cursorPositionRef.current = position;
													focusedInputRef.current = "warranty.type";
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															type: e.target.value,
														},
													}));
												}}
												onFocus={() => {
													focusedInputRef.current = "warranty.type";
												}}
												onSelect={(e: any) => {
													cursorPositionRef.current =
														e.target.selectionStart ?? 0;
												}}
												placeholder="e.g., Manufacturer, Extended"
											/>
										</div>
									</Col>
									<Col span={12}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Term (months)
											</label>
											<InputNumber
												value={
													formValues.warranty?.term ??
													asset.warranty?.term ??
													undefined
												}
												onChange={(value: number | null) =>
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															term: value || undefined,
														},
													}))
												}
												style={{ width: "100%" }}
												placeholder="Enter term in months"
												min={0}
											/>
										</div>
									</Col>
									<Col span={24}>
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													display: "block",
													marginBottom: "8px",
													fontWeight: 500,
												}}
											>
												Provider
											</label>
											<Input
												ref={(ref) => {
													if (ref) inputRefs.current["warranty.provider"] = ref;
												}}
												value={
													formValues.warranty?.provider ??
													asset.warranty?.provider ??
													""
												}
												onChange={(e) => {
													const target = e.target;
													const position = target.selectionStart ?? 0;
													cursorPositionRef.current = position;
													focusedInputRef.current = "warranty.provider";
													setFormValues((prev) => ({
														...prev,
														warranty: {
															...(prev.warranty || {}),
															provider: e.target.value,
														},
													}));
												}}
												onFocus={() => {
													focusedInputRef.current = "warranty.provider";
												}}
												onSelect={(e: any) => {
													cursorPositionRef.current =
														e.target.selectionStart ?? 0;
												}}
												placeholder="Warranty provider name"
											/>
										</div>
									</Col>
								</Row>
							) : (
								<Descriptions column={2} bordered size="small">
									<Descriptions.Item label="Has Warranty">
										{asset.warranty?.hasWarranty !== undefined ? (
											<Tag color={asset.warranty.hasWarranty ? "green" : "red"}>
												{asset.warranty.hasWarranty ? "Yes" : "No"}
											</Tag>
										) : (
											"Not specified"
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Warranty Status">
										{asset.warranty?.isWarrantyExpired !== undefined && (
											<Tag
												color={
													asset.warranty.isWarrantyExpired ? "red" : "green"
												}
											>
												{asset.warranty.isWarrantyExpired
													? "Expired"
													: "Active"}
											</Tag>
										)}
										{asset.warranty?.isNotFound && (
											<Tag color="orange">Not Found</Tag>
										)}
										{!asset.warranty?.isWarrantyExpired &&
											!asset.warranty?.isNotFound &&
											asset.warranty?.isWarrantyExpired === undefined &&
											"Not specified"}
									</Descriptions.Item>
									<Descriptions.Item label="Start Date">
										{formatDate(asset.warranty?.startDate)}
									</Descriptions.Item>
									<Descriptions.Item label="Expiration Date">
										{formatDate(asset.warranty?.expirationDate)}
									</Descriptions.Item>
									<Descriptions.Item label="Warranty Type">
										{asset.warranty?.type || "Not specified"}
									</Descriptions.Item>
									<Descriptions.Item label="Term">
										{asset.warranty?.term
											? `${asset.warranty.term} months`
											: "Not specified"}
									</Descriptions.Item>
									<Descriptions.Item label="Provider" span={2}>
										{asset.warranty?.provider || "Not specified"}
									</Descriptions.Item>
								</Descriptions>
							)}
						</SummaryCard>
					</Col>
				</Row>
			)}

			{/* =============== SYNC INFORMATION =============== */}
			{(asset.integrationSyncs && asset.integrationSyncs.length > 0) ||
			(asset.photoSync && asset.photoSync.id) ? (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					<Col xs={24}>
						<SummaryCard title="Sync Information" icon={<InfoCircleOutlined />}>
							<Descriptions column={2} bordered size="small">
								{asset.integrationSyncs &&
									asset.integrationSyncs.length > 0 &&
									asset.integrationSyncs.map((sync, index) => (
										<React.Fragment key={index}>
											<Descriptions.Item
												label={`Integration Sync ${index + 1} Status`}
											>
												<Tag
													color={
														sync.syncStatus === "success"
															? "green"
															: sync.syncStatus === "failed"
																? "red"
																: sync.syncStatus === "pending"
																	? "blue"
																	: "default"
													}
												>
													{sync.syncStatus?.toUpperCase()}
												</Tag>
											</Descriptions.Item>
											<Descriptions.Item label={`Last Synced`}>
												{sync.lastSyncedAt
													? new Date(sync.lastSyncedAt).toLocaleString()
													: "Never"}
											</Descriptions.Item>
										</React.Fragment>
									))}
								{asset.photoSync && asset.photoSync.id && (
									<>
										<Descriptions.Item label="Photo Sync Status">
											<Tag
												color={
													asset.photoSync.syncStatus === "success"
														? "green"
														: asset.photoSync.syncStatus === "failed"
															? "red"
															: asset.photoSync.syncStatus === "running"
																? "blue"
																: asset.photoSync.syncStatus === "skipped"
																	? "orange"
																	: "default"
												}
											>
												{asset.photoSync.syncStatus?.toUpperCase()}
											</Tag>
										</Descriptions.Item>
										<Descriptions.Item label="Last Photo Sync">
											{asset.photoSync.lastSyncedAt
												? new Date(
														asset.photoSync.lastSyncedAt,
													).toLocaleString()
												: "Never"}
										</Descriptions.Item>
										<Descriptions.Item label="Photos Uploaded">
											{asset.photoSync.photosUploaded || 0}
										</Descriptions.Item>
										<Descriptions.Item label="Photos Skipped">
											{asset.photoSync.photosSkipped || 0}
										</Descriptions.Item>
										<Descriptions.Item label="Photos Failed" span={2}>
											{asset.photoSync.photosFailed || 0}
										</Descriptions.Item>
									</>
								)}
							</Descriptions>
						</SummaryCard>
					</Col>
				</Row>
			) : null}

			{/* =============== UNIQUE FIELDS =============== */}
			{(isEditMode && orgUniqueFieldDefs.length > 0) ||
			(asset.uniqueFields && parseUniqueFields(asset.uniqueFields)) ? (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					<Col xs={24}>
						<SummaryCard title="Unique Fields" icon={<InfoCircleOutlined />}>
							{isEditMode ? (
								<Descriptions column={1} bordered size="small">
									{(() => {
										const existingFields = parseUniqueFields(asset.uniqueFields) || {};
										const allKeys = new Set([
											...orgUniqueFieldDefs.map((f) => f.name),
											...Object.keys(existingFields),
										]);
										return Array.from(allKeys).map((key) => {
											const fieldDef = orgUniqueFieldDefs.find((f) => f.name === key);
											const currentValue =
												formValues.uniqueFields?.[key] !== undefined
													? formValues.uniqueFields[key]
													: existingFields[key] ?? "";
											const label = fieldDef?.label ||
												key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1");

											return (
												<Descriptions.Item key={key} label={label}>
													{fieldDef?.type === "select" && fieldDef.options ? (
														<Select
															value={currentValue || undefined}
															onChange={(val) =>
																setFormValues((prev) => ({
																	...prev,
																	uniqueFields: {
																		...prev.uniqueFields,
																		[key]: val,
																	},
																}))
															}
															placeholder={fieldDef.placeholder || `Select ${label}`}
															style={{ width: "100%" }}
															allowClear
														>
															{fieldDef.options.map((opt) => (
																<Select.Option key={opt} value={opt}>
																	{opt}
																</Select.Option>
															))}
														</Select>
													) : (
														<Input
															key={`unique-field-${key}`}
															ref={(ref) => {
																if (ref)
																	inputRefs.current[`uniqueFields.${key}`] = ref;
															}}
															value={typeof currentValue === "object" ? JSON.stringify(currentValue) : String(currentValue)}
															onChange={(e) => {
																const target = e.target;
																const position = target.selectionStart ?? 0;
																cursorPositionRef.current = position;
																focusedInputRef.current = `uniqueFields.${key}`;
																setFormValues((prev) => ({
																	...prev,
																	uniqueFields: {
																		...prev.uniqueFields,
																		[key]: e.target.value,
																	},
																}));
															}}
															onFocus={() => {
																focusedInputRef.current = `uniqueFields.${key}`;
															}}
															onSelect={(e: any) => {
																cursorPositionRef.current = e.target.selectionStart ?? 0;
															}}
															placeholder={fieldDef?.placeholder || `Enter ${label}`}
														/>
													)}
												</Descriptions.Item>
											);
										});
									})()}
								</Descriptions>
							) : (
								<Descriptions column={1} bordered size="small">
									{Object.entries(
										parseUniqueFields(asset.uniqueFields) || {},
									).map(([key, value]) => {
										const fieldDef = orgUniqueFieldDefs.find((f) => f.name === key);
										const label = fieldDef?.label ||
											key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1");
										return (
											<Descriptions.Item key={key} label={label}>
												{typeof value === "object"
													? JSON.stringify(value)
													: String(value)}
											</Descriptions.Item>
										);
									})}
								</Descriptions>
							)}
						</SummaryCard>
					</Col>
				</Row>
			) : null}

			{/* =============== PHOTOS =============== */}
			<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
				<Col xs={24}>
					<SummaryCard title="Asset Photos" icon={<CameraOutlined />}>
						{renderPhotos()}
					</SummaryCard>
				</Col>
			</Row>

			{/* =============== NOTES =============== */}
			<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
				<Col xs={24}>
					<SummaryCard title="General Notes" icon={<InfoCircleOutlined />}>
						{isEditMode ? (
							renderEditableField(
								"General Notes",
								"generalNotes",
								asset.generalNotes,
								"textarea",
							)
						) : (
							<div
								style={{
									padding: "12px",
									backgroundColor: "#f9f9f9",
									borderRadius: "6px",
									whiteSpace: "pre-wrap",
								}}
							>
								{asset.generalNotes || "No notes available"}
							</div>
						)}
					</SummaryCard>
				</Col>
			</Row>

			{/* =============== REVISION HISTORY =============== */}
			{showRevisionHistory && (
				<Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
					<Col xs={24}>
						<SummaryCard title="Revision History" icon={<HistoryOutlined />}>
							{loadingRevisions ? (
								<div style={{ textAlign: "center", padding: "20px" }}>
									<Spin />
								</div>
							) : revisions.length === 0 ? (
								<div
									style={{
										textAlign: "center",
										padding: "20px",
										color: "#999",
										border: "1px dashed #d9d9d9",
										borderRadius: "6px",
									}}
								>
									<HistoryOutlined
										style={{ fontSize: "24px", marginBottom: "8px" }}
									/>
									<div>No revision history available</div>
								</div>
							) : (
								<Timeline>
									{revisions.map((revision) => {
										// Format field name for display
										const fieldLabel = revision.fieldName
											.replace(/([A-Z])/g, " $1")
											.replace(/^./, (str) => str.toUpperCase())
											.trim();

										// Get display values (prefer display values, fall back to raw values)
										const oldValueDisplay =
											revision.oldValueDisplay ??
											(revision.oldValue === null ||
											revision.oldValue === undefined
												? "Empty"
												: String(revision.oldValue));
										const newValueDisplay =
											revision.newValueDisplay ??
											(revision.newValue === null ||
											revision.newValue === undefined
												? "Empty"
												: String(revision.newValue));

										return (
											<Timeline.Item key={revision.id}>
												<div style={{ marginBottom: "8px" }}>
													<Text strong>{fieldLabel}</Text>
													<Text
														type="secondary"
														style={{ marginLeft: "8px", fontSize: "12px" }}
													>
														{formatDate(revision.revisionTimestamp)}
													</Text>
												</div>
												<div style={{ marginLeft: "16px", marginTop: "4px" }}>
													<Text
														type="secondary"
														delete
														style={{ marginRight: "8px" }}
													>
														{oldValueDisplay}
													</Text>
													<Text strong style={{ color: "#52c41a" }}>
														→ {newValueDisplay}
													</Text>
												</div>
												{revision.userName && (
													<div style={{ marginLeft: "16px", marginTop: "4px" }}>
														<Text type="secondary" style={{ fontSize: "12px" }}>
															Changed by: {revision.userName}
															{revision.userEmail && ` (${revision.userEmail})`}
														</Text>
													</div>
												)}
											</Timeline.Item>
										);
									})}
								</Timeline>
							)}
						</SummaryCard>
					</Col>
				</Row>
			)}
		</div>
	);
};

export { AssetDetail };
export type { AssetDetailProps, AssetAggregate };
